/**
 * User Context Provider
 *
 * Provides user profile, tenant management, and Plaid token status
 * for the authenticated user dashboard. Builds on top of AuthContext
 * for Supabase session management.
 *
 * @fileoverview React Context for user dashboard state management
 *
 * @example
 * // Wrap your dashboard with UserProvider
 * <UserProvider>
 *   <AppDashboard />
 * </UserProvider>
 *
 * // Access user state in components
 * const { user, selectedTenant, plaidTokenStatus } = useUser();
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';
import { useSupportMode, SupportTenant, SUPPORT_MODE_STORAGE_KEY } from '../shared/support-mode';
import { apiJson } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

/**
 * User profile interface matching backend AuthenticatedUser
 */
export interface UserProfile {
  /** User's unique identifier (UUID from Supabase auth.users) */
  id: string;
  /** User's email address */
  email: string;
  /** User's system-wide role */
  globalRole: 'ADMIN' | 'USER';
  /** Whether the user's account is currently active */
  isActive: boolean;
  /** User's display name */
  displayName?: string;
  /** URL to user's profile picture */
  avatarUrl?: string;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** User's phone number */
  phoneNumber?: string;
  /** Last login timestamp */
  lastLoginAt?: string;
}

/**
 * Tenant with membership information
 */
export interface TenantWithMembership {
  /** Tenant's unique identifier */
  id: string;
  /** Tenant name */
  name: string;
  /** URL-friendly slug */
  slug: string;
  /** Whether tenant is active */
  isActive: boolean;
  /** Whether current user owns this tenant */
  isOwner: boolean;
  /** Tenant creation date */
  createdAt: string;
  /** User's role within this tenant */
  tenantRole?: 'TENANT_ADMIN' | 'USER';
  /** Street address */
  address?: string | null;
  /** Address line 2 (apt, suite, etc.) */
  addressLine2?: string | null;
  /** City */
  city?: string | null;
  /** State or province */
  state?: string | null;
  /** ZIP or postal code */
  zipCode?: string | null;
  /** ISO 3166-1 alpha-2 country code */
  country?: string | null;
}

/**
 * Plaid token metadata (without the actual token)
 */
export interface PlaidTokenMetadata {
  /** Token record ID */
  id: string;
  /** Associated tenant ID */
  tenantId: string;
  /** Plaid item ID */
  itemId: string;
  /** Bank institution ID */
  institutionId?: string | null;
  /** Bank name */
  institutionName?: string | null;
  /** Token status */
  status: 'ACTIVE' | 'LOGIN_REQUIRED' | 'REVOKED';
  /** Token creation date */
  createdAt: string;
  /** Last update date */
  updatedAt: string;
  /** Plaid error code from last sync failure (e.g. ITEM_LOGIN_REQUIRED, INVALID_ACCESS_TOKEN) */
  errorCode?: string | null;
  /** When OAuth consent expires (ISO 8601) */
  consentExpirationTime?: string | null;
  /** Days until consent expires (null if no expiration) */
  daysUntilExpiration?: number | null;
  /** True if consent expires within 7 days */
  isExpirationImminent?: boolean;
}

/**
 * User context state interface
 */
export interface UserContextState {
  /** Current user profile, null if not loaded */
  user: UserProfile | null;
  /** List of user's tenants */
  tenants: TenantWithMembership[];
  /** Currently selected tenant */
  selectedTenant: TenantWithMembership | null;
  /** All Plaid tokens for selected tenant (multi-bank support) */
  plaidTokens: PlaidTokenMetadata[];
  /**
   * Primary Plaid token status for selected tenant (first token)
   * @deprecated Use plaidTokens array for multi-bank support
   */
  plaidTokenStatus: PlaidTokenMetadata | null;
  /** Currently selected account ID for filtering (empty = all accounts) */
  selectedAccountId: string;
  /** Whether the Plaid endpoint is unavailable/unsupported */
  plaidEndpointUnsupported: boolean;
  /** Loading state during data fetching */
  isLoading: boolean;
  /** Error message from last operation */
  error: string | null;
}

/**
 * User context methods interface
 */
export interface UserContextMethods {
  /**
   * Fetch user profile from backend
   * @returns Promise resolving when profile is fetched
   */
  fetchUserProfile: () => Promise<void>;

  /**
   * Fetch user's tenants from backend
   * @returns Promise resolving when tenants are fetched
   */
  fetchTenants: () => Promise<void>;

  /**
   * Select a tenant and fetch its Plaid token status
   * @param tenant - The tenant to select
   */
  selectTenant: (tenant: TenantWithMembership) => void;

  /**
   * Clear the selected tenant (used when exiting support mode)
   */
  clearSelectedTenant: () => void;

  /**
   * Fetch Plaid token status for selected tenant
   * @returns Promise resolving when status is fetched
   */
  fetchPlaidTokenStatus: () => Promise<void>;

  /**
   * Refresh all user data
   * @returns Promise resolving when all data is refreshed
   */
  refreshAll: () => Promise<void>;

  /**
   * Clear error state
   */
  clearError: () => void;

  /**
   * Set the selected account ID for filtering
   * @param accountId - Account ID to select, or empty string for all accounts
   */
  setSelectedAccountId: (accountId: string) => void;
}

/**
 * Complete user context type combining state and methods
 */
export type UserContextType = UserContextState & UserContextMethods;

/**
 * Default context value for initialization
 */
const defaultUserContext: UserContextType = {
  user: null,
  tenants: [],
  selectedTenant: null,
  plaidTokens: [],
  plaidTokenStatus: null,
  selectedAccountId: '',
  isLoading: true,
  error: null,
  fetchUserProfile: async () => {},
  fetchTenants: async () => {},
  selectTenant: () => {},
  clearSelectedTenant: () => {},
  fetchPlaidTokenStatus: async () => {},
  refreshAll: async () => {},
  clearError: () => {},
  setSelectedAccountId: () => {},
  plaidEndpointUnsupported: false,
};

/**
 * User Context instance
 */
const UserContext = createContext<UserContextType>(defaultUserContext);

/**
 * Props for UserProvider component
 */
interface UserProviderProps {
  /** Child components to wrap */
  children: ReactNode;
}

/**
 * User Provider Component
 *
 * Provides user profile, tenant management, and Plaid token status
 * to all child components via React Context.
 *
 * Features:
 * - Automatic user profile fetching on mount
 * - Tenant list management with selection
 * - Plaid token status for selected tenant
 * - Error handling and loading states
 *
 * @param props - Component props
 * @returns Provider component wrapping children
 *
 * @example
 * <UserProvider>
 *   <AppDashboard />
 * </UserProvider>
 */
/**
 * Helper to get support tenant from localStorage if in support mode
 * This allows userContext to sync with support mode on navigation
 */
const getSupportTenantFromStorage = (): SupportTenant | null => {
  try {
    const stored = localStorage.getItem(SUPPORT_MODE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.supportTenant || null;
    }
  } catch (err) {
    console.error('[UserContext] Failed to read support mode from storage:', err);
  }
  return null;
};

/**
 * Convert SupportTenant to TenantWithMembership
 */
const mapSupportTenantToMembership = (tenant: SupportTenant): TenantWithMembership => ({
  id: tenant.id,
  name: tenant.name,
  slug: tenant.slug,
  isActive: tenant.isActive,
  isOwner: false, // Admin viewing in support mode is not the owner
  createdAt: tenant.createdAt,
});

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  // Get session from AuthContext instead of calling getSession() again
  const { session: authSession, mfaRequired, mfaChecked } = useAuthContext();
  const { supportTenant } = useSupportMode();

  const [user, setUser] = useState<UserProfile | null>(null);
  const [tenants, setTenants] = useState<TenantWithMembership[]>([]);
  // Initialize selectedTenant from localStorage if in support mode
  // This ensures support mode persists across page navigation
  const [selectedTenant, setSelectedTenant] = useState<TenantWithMembership | null>(() => {
    const supportTenant = getSupportTenantFromStorage();
    if (supportTenant) {

      return mapSupportTenantToMembership(supportTenant);
    }
    return null;
  });
  const [plaidTokens, setPlaidTokens] = useState<PlaidTokenMetadata[]>([]);
  const [plaidTokenStatus, setPlaidTokenStatus] = useState<PlaidTokenMetadata | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [plaidEndpointUnsupported, setPlaidEndpointUnsupported] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const plaidUnsupportedRef = useRef(false);
  const prevUserIdRef = useRef<string | null>(null);
  // Whether fetchUserAndTenants has run since MFA was last confirmed
  // settled for the current user. See the effect below for why this can't
  // just be "did the user id change" (userChanged) — that's blind to MFA
  // clearing without a user-id change in between.
  const hasFetchedForSessionRef = useRef(false);

  /**
   * Get access token from AuthContext session (no additional getSession() call)
   */
  const getAccessToken = useCallback((): string | null => {

    return authSession?.access_token ?? null;
  }, [authSession]);

  /**
   * Fetch user profile from backend
   */
  const fetchUserProfile = useCallback(async (): Promise<void> => {
    try {
      const accessToken = getAccessToken();

      if (!accessToken) {
        setUser(null);
        return;
      }

      const data: UserProfile = await apiJson<UserProfile>('/api/auth/me', {}, accessToken);

      setUser(data);
    } catch (err) {
      console.error('[UserContext] fetchUserProfile error:', err);
      setError(toUserMessage(err, "We couldn't load your profile. Please try again."));
      setUser(null);
    }
  }, [getAccessToken]);

  /**
   * Fetch user's tenants from backend
   */
  const fetchTenants = useCallback(async (): Promise<void> => {
    try {
      const accessToken = getAccessToken();

      if (!accessToken) {
        setTenants([]);
        return;
      }

      const data: TenantWithMembership[] = await apiJson<TenantWithMembership[]>('/api/tenants', {}, accessToken);

      setTenants(data);

      // Check if we're in support mode - if so, try to restore the support tenant
      const supportTenant = getSupportTenantFromStorage();
      
      if (supportTenant) {
        setSelectedTenant(mapSupportTenantToMembership(supportTenant));
      } else if (data.length > 0) {
        // Check if user just signed up - select their newly created tenant
        const signupTenantId = localStorage.getItem('xacto_signup_tenant_id');
        if (signupTenantId) {
          localStorage.removeItem('xacto_signup_tenant_id');
          const signupTenant = data.find((t) => t.id === signupTenantId);
          if (signupTenant) {
            setSelectedTenant(signupTenant);
            return;
          }
        }
        // Auto-select first tenant if not in support mode
        // Use functional update to avoid dependency on selectedTenant
        setSelectedTenant((current) => current ?? data[0]);
      }
    } catch (err) {
      console.error('[UserContext] fetchTenants error:', err);
      setError(toUserMessage(err, "We couldn't load tenants. Please try again."));
      setTenants([]);
    }
  }, [getAccessToken]);

  /**
   * Select a tenant and trigger Plaid token status fetch
   */
  const selectTenant = useCallback((tenant: TenantWithMembership): void => {
    setSelectedTenant(tenant);
    setPlaidTokens([]); // Clear previous tokens
    setPlaidTokenStatus(null); // Clear previous status
    setSelectedAccountId(''); // Clear account filter when switching tenants
  }, []);

  /**
   * Clear the selected tenant (used when exiting support mode)
   */
  const clearSelectedTenant = useCallback((): void => {
    setSelectedTenant(null);
    setPlaidTokens([]);
    setPlaidTokenStatus(null);
    setSelectedAccountId('');
  }, []);

  /**
   * Fetch Plaid token status for selected tenant
   * Fetches all tokens (multi-bank) and sets both the array and legacy single token
   */
  const fetchPlaidTokenStatus = useCallback(async (): Promise<void> => {
    if (!selectedTenant) {
      setPlaidTokens([]);
      setPlaidTokenStatus(null);
      return;
    }

    if (plaidUnsupportedRef.current) {

      return;
    }

    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        setPlaidTokens([]);
        setPlaidTokenStatus(null);
        return;
      }

      // Fetch all tokens (multi-bank support)
      const data: PlaidTokenMetadata[] = await apiJson<PlaidTokenMetadata[]>(
        '/api/plaid-token',
        {
          headers: {
            'x-tenant-id': selectedTenant.id,
          },
        },
        accessToken
      );

      // Set the full array
      setPlaidTokens(data);

      const activeToken = data.find((token) => token.status === 'ACTIVE') ?? null;
      const primaryToken = activeToken ?? (data.length > 0 ? data[0] : null);

      console.log('[UserContext] Fetched Plaid tokens', {
        tenantId: selectedTenant.id,
        total: data.length,
        active: data.filter((token) => token.status === 'ACTIVE').length,
        loginRequired: data.filter((token) => token.status === 'LOGIN_REQUIRED').length,
        primaryTokenId: primaryToken?.id ?? null,
        primaryStatus: primaryToken?.status ?? null,
      });

      // Set legacy single token, prioritizing ACTIVE token for UI compatibility
      setPlaidTokenStatus(primaryToken);
    } catch (err) {
      // Don't set error for 404 - it's expected when no token exists
      // 404 is expected when no token exists - this is normal after reset or before connecting
      // Don't mark as unsupported, just set status to null so UI shows "Connect Bank"
      if (err instanceof Error && err.message.includes('404')) {

        setPlaidTokens([]);
        setPlaidTokenStatus(null);
        return;
      }
      setPlaidTokens([]);
      setPlaidTokenStatus(null);
    }
  }, [getAccessToken, selectedTenant]);

  /**
   * Refresh all user data
   */
  /**
   * Fetch user and tenants (stable reference for initial load)
   */
  const fetchUserAndTenants = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      await fetchUserProfile();
      await fetchTenants();
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken, fetchUserProfile, fetchTenants]);

  /**
   * Refresh all user data
   */
  const refreshAll = useCallback(async (): Promise<void> => {
    await fetchUserAndTenants();

    if (selectedTenant) {
      await fetchPlaidTokenStatus();
    }
  }, [fetchUserAndTenants, selectedTenant, fetchPlaidTokenStatus]);

  /**
   * Clear error state
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Initialize user data on mount, when session changes, and once MFA
   * clears for the current session.
   *
   * MUST wait for `mfaChecked` before fetching anything. `authSession`
   * becoming non-null does not mean MFA is settled — sign-in sets the
   * Supabase session immediately, before the MFA challenge check even
   * runs — and `mfaRequired` starts `false` before that check resolves too,
   * indistinguishable from "checked and not required." Fetching in that
   * window hits /api/tenants while MfaGuard still denies it, which used to
   * leave `error` stuck on "We couldn't load tenants" with no way to
   * recover short of a full page reload: completing MFA afterward does NOT
   * change `authSession` at all (it's tracked server-side, independent of
   * the Supabase JWT), so `userChanged` below would never go true again for
   * the same user, and nothing else in this effect would retrigger the
   * fetch — a reload worked only because it re-ran this effect from
   * scratch, by which point MFA was already satisfied.
   */
  useEffect(() => {
    if (!authSession) {
      // Signed out — clear everything
      prevUserIdRef.current = null;
      hasFetchedForSessionRef.current = false;
      setUser(null);
      setTenants([]);
      setSelectedTenant(null);
      setPlaidTokens([]);
      setPlaidTokenStatus(null);
      setSelectedAccountId('');
      setIsLoading(false);
      return;
    }

    if (!mfaChecked || mfaRequired) {
      return;
    }

    const currentUserId = authSession.user?.id ?? null;
    const userChanged = currentUserId !== prevUserIdRef.current;
    prevUserIdRef.current = currentUserId;

    // Fetch on a genuine user change (sign-in, first load), OR the first
    // time this effect finds MFA settled+satisfied for the current user —
    // covering the case where MFA just got verified with no user-id change
    // in between, which userChanged alone can't see.
    if (userChanged || !hasFetchedForSessionRef.current) {
      hasFetchedForSessionRef.current = true;
      // TOKEN_REFRESHED (same user, new access_token) hits neither branch
      // above, so it correctly still skips — no data reload needed.
      const plaidLinkActive = !!sessionStorage.getItem('xacto_plaid_link_token');
      if (!plaidLinkActive) {
        fetchUserAndTenants();
      }
    }
  }, [authSession, mfaChecked, mfaRequired, fetchUserAndTenants]);

  /**
   * Fetch Plaid token status when selected tenant changes
   */
  useEffect(() => {
    if (selectedTenant) {
      fetchPlaidTokenStatus();
    }
  }, [selectedTenant, fetchPlaidTokenStatus]);

  /**
   * Poll token status every 30 minutes to detect disconnections
   * and consent expirations without requiring page refresh.
   */
  useEffect(() => {
    if (!selectedTenant) return;

    const POLL_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
    const intervalId = setInterval(() => {
      fetchPlaidTokenStatus();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [selectedTenant, fetchPlaidTokenStatus]);

  /**
   * React to support mode changes directly via SupportModeContext state.
   * This works within the same tab (unlike window.storage events which only
   * fire across tabs). Uses a ref to detect transitions (enter vs exit).
   */
  const prevSupportTenantIdRef = useRef<string | null>(supportTenant?.id ?? null);
  useEffect(() => {
    const prevId = prevSupportTenantIdRef.current;
    const currId = supportTenant?.id ?? null;
    prevSupportTenantIdRef.current = currId;

    if (supportTenant) {
      setSelectedTenant(mapSupportTenantToMembership(supportTenant));
    } else if (prevId !== null && currId === null) {
      // Support mode just exited — reset to first user tenant
      setSelectedTenant(tenants.length > 0 ? tenants[0] : null);
    }
  }, [supportTenant, tenants]);

  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo<UserContextType>(
    () => ({
      user,
      tenants,
      selectedTenant,
      plaidTokens,
      plaidTokenStatus,
      selectedAccountId,
      isLoading,
      error,
      fetchUserProfile,
      fetchTenants,
      selectTenant,
      clearSelectedTenant,
      fetchPlaidTokenStatus,
      refreshAll,
      clearError,
      setSelectedAccountId,
      plaidEndpointUnsupported,
    }),
    [
      user,
      tenants,
      selectedTenant,
      plaidTokens,
      plaidTokenStatus,
      selectedAccountId,
      isLoading,
      error,
      fetchUserProfile,
      fetchTenants,
      selectTenant,
      clearSelectedTenant,
      fetchPlaidTokenStatus,
      refreshAll,
      clearError,
      setSelectedAccountId,
      plaidEndpointUnsupported,
    ]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Hook to access user context
 *
 * Must be used within a UserProvider component.
 *
 * @returns User context with profile, tenants, and Plaid token status
 * @throws Error if used outside of UserProvider
 *
 * @example
 * const { user, selectedTenant, plaidTokenStatus } = useUser();
 *
 * if (isLoading) return <Spinner />;
 * return <Dashboard user={user} tenant={selectedTenant} />;
 */
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export default UserContext;
